import { useEffect, useState } from 'react';

/**
 * Tailwind-ish breakpoint constants matching the CSS media queries in
 * `apps/web/src/styles/global.css`. Centralised here so JS and CSS stay
 * in sync.
 */
export const BREAKPOINTS = {
  mobile: '(max-width: 639.98px)',
  tablet: '(min-width: 640px) and (max-width: 1023.98px)',
  desktop: '(min-width: 1024px)',
} as const;

export type BreakpointKey = keyof typeof BREAKPOINTS;

/**
 * SSR-safe `matchMedia` subscription. Returns `false` on the first render
 * when `window` is undefined (SSR) or `matchMedia` is missing (jsdom
 * without the helper polyfill); the value updates on the next tick once
 * the listener fires.
 */
export function useMediaQuery(query: string): boolean {
  const get = () => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  };
  const [matches, setMatches] = useState<boolean>(get);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    // addEventListener is the modern API; fallback to addListener for older mocks.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
}

export const useIsMobile = (): boolean => useMediaQuery(BREAKPOINTS.mobile);
export const useIsTablet = (): boolean => useMediaQuery(BREAKPOINTS.tablet);
export const useIsDesktop = (): boolean => useMediaQuery(BREAKPOINTS.desktop);
// Split-pane invoice list activates at ≥1280px (REQ-FE-029a). Below this
// the screen collapses to a single-column list, matching the original
// layout and the InvoiceListScreen.split-pane.test.tsx contract.
export const useIsWide = (): boolean => useMediaQuery('(min-width: 1280px)');
